import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import MultiLevelSelect from '../../../../../../components/MultiLevelSelect'
import FieldLabelWrapper from '../../../../../../features/components/Form/FieldLabelWrapper'
import AdditionalInterestCategoriesMultiselect, {
  INTEREST_KEYWORD_IDS
} from '../../../../UploadTikTokAdCreative/UploadTikTokAdCreativeForm/LineItemForm/Steps/CustomAudiencesStep/TikTokUploadCreativeAudienceFields/TikTokAdvancedAudienceTargetingSection/AdditionalInterestCategoriesMultiselect'
import PurchaseIntentionCategoriesMultiselect, {
  PURCHASE_INTENTION_KEYWORD_IDS
} from '../../../../UploadTikTokAdCreative/UploadTikTokAdCreativeForm/LineItemForm/Steps/CustomAudiencesStep/TikTokUploadCreativeAudienceFields/TikTokAdvancedAudienceTargetingSection/PurchaseIntentionCategoriesMultiselect'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import { useEditSectionFormSubmit } from '../../../../../../hooks/formHooks/useEditSectionFormSubmit'

import { filterObjectEmptyValues } from '../../../../../../helpers/common'
import { getMultiLevelSelectPayloadValues } from '../../../../../../components/MultiLevelSelect/formatters'
import { formatValuesToFE } from './formatters'
import { getShowPurchaseIntentionCategories } from '../../../../UploadTikTokAdCreative/UploadTikTokAdCreativeForm/LineItemForm/Steps/CustomAudiencesStep/TikTokUploadCreativeAudienceFields/TikTokAdvancedAudienceTargetingSection/helpers'

import { updateLineItem, clearUpdateLineItem } from '../../../../../../modules/actions/lineItems'
import {
  lineItemUpdateErrorSelector,
  lineItemUpdateIsLoadingSelector,
  lineItemWasUpdatedSelector
} from '../../../../../../modules/selectors/lineItems'
import { selectedTikTokAdAccountSelector } from '../../../../../../modules/selectors/adAccounts'
import {
  getAdditionalInterestCategoriesByIdsSelector,
  getPurchaseIntentionCategoriesByIdsSelector,
  tikTokInterestsSelector
} from '../../../../../../modules/selectors/tikTokConstants'
import { campaignSelector } from '../../../../../../modules/selectors/campaigns'

import { TIKTOK_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { HISTORY_META_EDIT_SECTION } from '../../../../../../constants/changeHistory'
import { INTERESTS } from '../../../../../ReusableFormFields/InterestsFields/fields'

const AudienceSectionForm = ({ editItemData: lineItem, title, ...formProps }) => {
  const dispatch = useDispatch()

  const { id: tiktokAdAccountId } = useSelector(selectedTikTokAdAccountSelector)
  const lineItemWasUpdated = useSelector(lineItemWasUpdatedSelector)
  const tikTokInterests = useSelector(tikTokInterestsSelector)
  const additionalInterestCategoriesByIds = useSelector(getAdditionalInterestCategoriesByIdsSelector)
  const purchaseIntentionCategoriesByIds = useSelector(getPurchaseIntentionCategoriesByIdsSelector)
  const campaign = useSelector(campaignSelector)
  const { objective: campaignObjective } = campaign

  const initialValues = useMemo(
    () =>
      formatValuesToFE({
        lineItem,
        interestOptions: tikTokInterests,
        additionalInterestCategoriesByIds,
        purchaseIntentionCategoriesByIds
      }),
    [lineItem, tikTokInterests, additionalInterestCategoriesByIds, purchaseIntentionCategoriesByIds]
  )

  const showPurchaseIntentionCategories = useMemo(() => {
    return getShowPurchaseIntentionCategories(campaignObjective)
  }, [campaignObjective])

  const handleSubmit = useCallback(
    values => {
      // as we use PUT for update tiktok campaign, we should pass full data in payload
      dispatch(
        updateLineItem(
          {
            ...filterObjectEmptyValues(lineItem),
            account: tiktokAdAccountId,
            interest_category: getMultiLevelSelectPayloadValues(values[INTERESTS], tikTokInterests),
            [INTEREST_KEYWORD_IDS]: values[INTEREST_KEYWORD_IDS].map(item => item.value),
            [PURCHASE_INTENTION_KEYWORD_IDS]: values[PURCHASE_INTENTION_KEYWORD_IDS].map(item => item.value),

            meta: {
              [HISTORY_META_EDIT_SECTION]: title
            }
          },
          lineItem.id,
          TIKTOK_PLATFORM,
          {
            requestMethod: 'PUT'
          }
        )
      )
    },
    [dispatch, lineItem, tiktokAdAccountId, tikTokInterests, title]
  )

  const { onSubmit, manualSuccessSubmit } = useEditSectionFormSubmit({ initialValues, handleSubmit })

  const formik = useFormik({
    initialValues,
    onSubmit,
    enableReinitialize: true
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const { setFieldValue, values } = formik

  const hasInterestKeywords = values[INTEREST_KEYWORD_IDS]?.length > 0
  const hasPurchaseIntentionKeywords = values[PURCHASE_INTENTION_KEYWORD_IDS]?.length > 0

  const handleClearUpdateLineItem = useCallback(() => {
    dispatch(clearUpdateLineItem())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={lineItemWasUpdated || manualSuccessSubmit}
      clearEditItem={handleClearUpdateLineItem}
      errorSelector={lineItemUpdateErrorSelector}
      isLoadingSelector={lineItemUpdateIsLoadingSelector}
      {...formProps}
    >
      <FieldLabelWrapper label="Interests">
        <MultiLevelSelect
          placeholder="Add an Interest"
          setFieldValue={setFieldValue}
          value={values[INTERESTS]}
          name={INTERESTS}
          options={tikTokInterests}
        />
      </FieldLabelWrapper>
      <FieldLabelWrapper label="Additional Interest Categories">
        <AdditionalInterestCategoriesMultiselect formik={formik} disabled={hasPurchaseIntentionKeywords} />
      </FieldLabelWrapper>
      {showPurchaseIntentionCategories && (
        <FieldLabelWrapper label="Purchase Intention Categories">
          <PurchaseIntentionCategoriesMultiselect formik={formik} disabled={hasInterestKeywords} />
        </FieldLabelWrapper>
      )}
    </EditForm>
  )
}

export default AudienceSectionForm
